/**
 * Copyright (c) 2017-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign, __extends } from "tslib";
import { SymmetryOperator } from '../../../mol-math/geometry/symmetry-operator';
import { Model } from '../model';
import { GridLookup3D, Spacegroup } from '../../../mol-math/geometry';
import { computeIntraUnitBonds } from './unit/bonds';
import { BitFlags } from '../../../mol-util';
import { UnitRings } from './unit/rings';
import { IntMap, SortedArray, Segmentation } from '../../../mol-data/int';
import { hash2, hashFnv32a } from '../../../mol-data/util';
import { getAtomicPolymerElements, getCoarsePolymerElements, getAtomicGapElements, getCoarseGapElements, getNucleotideElements, getProteinElements } from './util/polymer';
import { getPrincipalAxes } from './util/principal-axes';
import { getBoundary } from '../../../mol-math/geometry/boundary';
import { Mat4, Vec3 } from '../../../mol-math/linear-algebra';
import { IndexPairBonds } from '../../../mol-model-formats/structure/property/bonds/index-pair';
import { ElementSetIntraBondCache } from './unit/bonds/element-set-intra-bond-cache';
import { ModelSymmetry } from '../../../mol-model-formats/structure/property/symmetry';
var Unit;

(function (Unit) {
  function isAtomic(u) {
    return u.kind === 0
    /* Atomic */
    ;
  }

  Unit.isAtomic = isAtomic;

  function isCoarse(u) {
    return u.kind === 1
    /* Spheres */
    || u.kind === 2
    /* Gaussians */
    ;
  }

  Unit.isCoarse = isCoarse;

  function isSpheres(u) {
    return u.kind === 1
    /* Spheres */
    ;
  }

  Unit.isSpheres = isSpheres;

  function isGaussians(u) {
    return u.kind === 2
    /* Gaussians */
    ;
  }

  Unit.isGaussians = isGaussians;

  function create(id, invariantId, chainGroupId, traits, kind, model, operator, elements, props) {
    switch (kind) {
      case 0
      /* Atomic */
      :
        return new Atomic(id, invariantId, chainGroupId, traits, model, elements, SymmetryOperator.createMapping(operator, model.atomicConformation, void 0), props !== null && props !== void 0 ? props : AtomicProperties());

      case 1
      /* Spheres */
      :
        return createCoarse(id, invariantId, chainGroupId, traits, model, 1
        /* Spheres */
        , elements, SymmetryOperator.createMapping(operator, model.coarseConformation.spheres, getSphereRadiusFunc(model)), props !== null && props !== void 0 ? props : CoarseProperties());

      case 2
      /* Gaussians */
      :
        return createCoarse(id, invariantId, chainGroupId, traits, model, 2
        /* Gaussians */
        , elements, SymmetryOperator.createMapping(operator, model.coarseConformation.gaussians, getGaussianRadiusFunc(model)), props !== null && props !== void 0 ? props : CoarseProperties());
    }
  }

  Unit.create = create;

  function getUnitIndexMap(units) {
    var unitIndexMap = IntMap.Mutable();

    for (var i = 0, _i = units.length; i < _i; i++) {
      unitIndexMap.set(units[i].id, i);
    }

    return unitIndexMap;
  }

  function getTransformHash(units) {
    var ids = [];

    for (var i = 0, _i = units.length; i < _i; i++) {
      ids.push(units[i].id);
    }

    return hashFnv32a(ids);
  }

  function SymmetryGroup(units) {
    var props = {};
    return {
      elements: units[0].elements,
      units: units,

      get unitIndexMap() {
        if (props.unitIndexMap) return props.unitIndexMap;
        props.unitIndexMap = getUnitIndexMap(units);
        return props.unitIndexMap;
      },

      hashCode: hashUnit(units[0]),
      transformHash: getTransformHash(units)
    };
  }

  Unit.SymmetryGroup = SymmetryGroup;

  (function (SymmetryGroup) {
    function areInvariantElementsEqual(a, b) {
      if (a.hashCode !== b.hashCode) return false;
      return SortedArray.areEqual(a.elements, b.elements);
    }

    SymmetryGroup.areInvariantElementsEqual = areInvariantElementsEqual;

    function getUnitSymmetryGroupsIndexMap(symmetryGroups) {
      var unitSymmetryGroupsIndexMap = IntMap.Mutable();

      for (var i = 0, il = symmetryGroups.length; i < il; ++i) {
        var sg = symmetryGroups[i];

        for (var j = 0, jl = sg.units.length; j < jl; ++j) {
          unitSymmetryGroupsIndexMap.set(sg.units[j].id, i);
        }
      }

      return unitSymmetryGroupsIndexMap;
    }

    SymmetryGroup.getUnitSymmetryGroupsIndexMap = getUnitSymmetryGroupsIndexMap;
  })(SymmetryGroup = Unit.SymmetryGroup || (Unit.SymmetryGroup = {}));

  function conformationId(unit) {
    return Unit.isAtomic(unit) ? unit.model.atomicConformation.id : unit.model.coarseConformation.id;
  }

  Unit.conformationId = conformationId;

  function hashUnit(u) {
    return hash2(u.invariantId, SortedArray.hashCode(u.elements));
  }

  Unit.hashUnit = hashUnit;
  var Traits;

  (function (Traits) {
    Traits.is = BitFlags.has;
    Traits.create = BitFlags.create;
  })(Traits = Unit.Traits || (Unit.Traits = {}));

  function BaseProperties() {
    return {};
  }

  function getSphereRadiusFunc(model) {
    var r = model.coarseConformation.spheres.radius;
    return function (i) {
      return r[i];
    };
  }

  function getGaussianRadiusFunc(model) {
    // TODO: compute radius for gaussians
    return function (i) {
      return 0;
    };
  }
  /**
   * A bulding block of a structure that corresponds
   * to a "natural group of atoms" (most often a "chain")
   * together with a transformation (rotation and translation)
   * that is dynamically applied to the underlying atom set.
   *
   * An atom set can be referenced by multiple different units which
   * makes construction of assemblies and spacegroups very efficient.
   */


  var Atomic =
  /** @class */
  function () {
    function Atomic(id, invariantId, chainGroupId, traits, model, elements, conformation, props) {
      this.kind = 0
      /* Atomic */
      ;
      this.objectPrimitive = 'atomistic';
      this.id = id;
      this.invariantId = invariantId;
      this.chainGroupId = chainGroupId;
      this.traits = traits;
      this.model = model;
      this.elements = elements;
      this.conformation = conformation;
      this.residueIndex = model.atomicHierarchy.residueAtomSegments.index;
      this.chainIndex = model.atomicHierarchy.chainAtomSegments.index;
      this.props = props;
    }

    Atomic.prototype.getChild = function (elements) {
      if (elements.length === this.elements.length) return this;
      return new Atomic(this.id, this.invariantId, this.chainGroupId, this.traits, this.model, elements, this.conformation, AtomicProperties());
    };

    Atomic.prototype.applyOperator = function (id, operator, dontCompose) {
      if (dontCompose === void 0) {
        dontCompose = false;
      }

      var op = dontCompose ? operator : SymmetryOperator.compose(this.conformation.operator, operator);
      return new Atomic(id, this.invariantId, this.chainGroupId, this.traits, this.model, this.elements, SymmetryOperator.createMapping(op, this.model.atomicConformation, this.conformation.r), this.props);
    };

    Atomic.prototype.remapModel = function (model, props) {
      if (!props) {
        props = __assign(__assign({}, this.props), {
          bonds: tryRemapBonds(this, this.props.bonds, model)
        });

        if (!Unit.isSameConformation(this, model)) {
          props.boundary = undefined;
          props.lookup3d = undefined;
          props.principalAxes = undefined;
        }
      }

      var operator = this.conformation.operator;
      var symmetry = ModelSymmetry.Provider.get(model);

      if (operator.spgrOp !== -1 && symmetry && symmetry !== ModelSymmetry.Provider.get(this.model)) {
        var _a = operator.hkl,
            i = _a[0],
            j = _a[1],
            k = _a[2];
        var toFractional = symmetry.spacegroup.cell.toFractional;
        var ref = Vec3.transformMat4(Vec3(), Model.getCenter(model), toFractional);
        operator = Spacegroup.getSymmetryOperatorRef(symmetry.spacegroup, operator.spgrOp, i, j, k, ref);
      }

      var conformation = this.model.atomicConformation !== model.atomicConformation || operator !== this.conformation.operator ? SymmetryOperator.createMapping(operator, model.atomicConformation) : this.conformation;
      return new Atomic(this.id, this.invariantId, this.chainGroupId, this.traits, model, this.elements, conformation, props);
    };

    Object.defineProperty(Atomic.prototype, "boundary", {
      get: function () {
        if (this.props.boundary) return this.props.boundary;
        var _a = this.model.atomicConformation,
            x = _a.x,
            y = _a.y,
            z = _a.z;
        this.props.boundary = getBoundary({
          x: x,
          y: y,
          z: z,
          indices: this.elements
        });
        return this.props.boundary;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "lookup3d", {
      get: function () {
        if (this.props.lookup3d) return this.props.lookup3d;
        var _a = this.model.atomicConformation,
            x = _a.x,
            y = _a.y,
            z = _a.z;
        this.props.lookup3d = GridLookup3D({
          x: x,
          y: y,
          z: z,
          indices: this.elements
        }, this.boundary);
        return this.props.lookup3d;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "principalAxes", {
      get: function () {
        if (this.props.principalAxes) return this.props.principalAxes;
        this.props.principalAxes = getPrincipalAxes(this);
        return this.props.principalAxes;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "bonds", {
      get: function () {
        if (this.props.bonds) return this.props.bonds;
        var cache = ElementSetIntraBondCache.get(this.model);
        var bonds = cache.get(this.elements);

        if (!bonds) {
          bonds = computeIntraUnitBonds(this);
          cache.set(this.elements, bonds);
        }

        this.props.bonds = bonds;
        return this.props.bonds;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "rings", {
      get: function () {
        if (this.props.rings) return this.props.rings;
        this.props.rings = UnitRings.create(this);
        return this.props.rings;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "polymerElements", {
      get: function () {
        if (this.props.polymerElements) return this.props.polymerElements;
        this.props.polymerElements = getAtomicPolymerElements(this);
        return this.props.polymerElements;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "gapElements", {
      get: function () {
        if (this.props.gapElements) return this.props.gapElements;
        this.props.gapElements = getAtomicGapElements(this);
        return this.props.gapElements;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "nucleotideElements", {
      get: function () {
        if (this.props.nucleotideElements) return this.props.nucleotideElements;
        this.props.nucleotideElements = getNucleotideElements(this);
        return this.props.nucleotideElements;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "proteinElements", {
      get: function () {
        if (this.props.proteinElements) return this.props.proteinElements;
        this.props.proteinElements = getProteinElements(this);
        return this.props.proteinElements;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Atomic.prototype, "residueCount", {
      get: function () {
        if (this.props.residueCount !== undefined) return this.props.residueCount;
        var residueCount = 0;
        var residueIt = Segmentation.transientSegments(this.model.atomicHierarchy.residueAtomSegments, this.elements);

        while (residueIt.hasNext) {
          residueIt.move();
          residueCount += 1;
        }

        this.props.residueCount = residueCount;
        return this.props.residueCount;
      },
      enumerable: false,
      configurable: true
    });

    Atomic.prototype.getResidueIndex = function (elementIndex) {
      return this.residueIndex[this.elements[elementIndex]];
    };

    return Atomic;
  }();

  Unit.Atomic = Atomic;

  function AtomicProperties() {
    return BaseProperties();
  }

  var Coarse =
  /** @class */
  function () {
    function Coarse(id, invariantId, chainGroupId, traits, model, kind, elements, conformation, props) {
      this.kind = kind;
      this.objectPrimitive = kind === 1
      /* Spheres */
      ? 'sphere' : 'gaussian';
      this.id = id;
      this.invariantId = invariantId;
      this.chainGroupId = chainGroupId;
      this.traits = traits;
      this.model = model;
      this.elements = elements;
      this.conformation = conformation;
      this.coarseElements = kind === 1
      /* Spheres */
      ? model.coarseHierarchy.spheres : model.coarseHierarchy.gaussians;
      this.coarseConformation = kind === 1
      /* Spheres */
      ? model.coarseConformation.spheres : model.coarseConformation.gaussians;
      this.props = props;
    }

    Coarse.prototype.getChild = function (elements) {
      if (elements.length === this.elements.length) return this
      /** lets call this an ugly temporary hack */
      ;
      return createCoarse(this.id, this.invariantId, this.chainGroupId, this.traits, this.model, this.kind, elements, this.conformation, CoarseProperties());
    };

    Coarse.prototype.applyOperator = function (id, operator, dontCompose) {
      if (dontCompose === void 0) {
        dontCompose = false;
      }

      var op = dontCompose ? operator : SymmetryOperator.compose(this.conformation.operator, operator);
      return createCoarse(id, this.invariantId, this.chainGroupId, this.traits, this.model, this.kind, this.elements, SymmetryOperator.createMapping(op, this.getCoarseConformation(), this.conformation.r), this.props);
    };

    Coarse.prototype.remapModel = function (model, props) {
      var coarseConformation = this.getCoarseConformation();
      var modelCoarseConformation = getCoarseConformation(this.kind, model);

      if (!props) {
        props = __assign({}, this.props);

        if (!Unit.isSameConformation(this, model)) {
          // TODO get rid of casting
          props.boundary = undefined;
          props.lookup3d = undefined;
          props.principalAxes = undefined;
        }
      }

      var conformation = coarseConformation !== modelCoarseConformation ? SymmetryOperator.createMapping(this.conformation.operator, modelCoarseConformation) : this.conformation;
      return new Coarse(this.id, this.invariantId, this.chainGroupId, this.traits, model, this.kind, this.elements, conformation, props); // TODO get rid of casting
    };

    Object.defineProperty(Coarse.prototype, "boundary", {
      get: function () {
        if (this.props.boundary) return this.props.boundary; // TODO: support sphere radius?

        var _a = this.getCoarseConformation(),
            x = _a.x,
            y = _a.y,
            z = _a.z;

        this.props.boundary = getBoundary({
          x: x,
          y: y,
          z: z,
          indices: this.elements
        });
        return this.props.boundary;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Coarse.prototype, "lookup3d", {
      get: function () {
        if (this.props.lookup3d) return this.props.lookup3d; // TODO: support sphere radius?

        var _a = this.getCoarseConformation(),
            x = _a.x,
            y = _a.y,
            z = _a.z;

        this.props.lookup3d = GridLookup3D({
          x: x,
          y: y,
          z: z,
          indices: this.elements
        }, this.boundary);
        return this.props.lookup3d;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Coarse.prototype, "principalAxes", {
      get: function () {
        if (this.props.principalAxes) return this.props.principalAxes;
        this.props.principalAxes = getPrincipalAxes(this); // TODO get rid of casting

        return this.props.principalAxes;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Coarse.prototype, "polymerElements", {
      get: function () {
        if (this.props.polymerElements) return this.props.polymerElements;
        this.props.polymerElements = getCoarsePolymerElements(this); // TODO get rid of casting

        return this.props.polymerElements;
      },
      enumerable: false,
      configurable: true
    });
    Object.defineProperty(Coarse.prototype, "gapElements", {
      get: function () {
        if (this.props.gapElements) return this.props.gapElements;
        this.props.gapElements = getCoarseGapElements(this); // TODO get rid of casting

        return this.props.gapElements;
      },
      enumerable: false,
      configurable: true
    });

    Coarse.prototype.getCoarseConformation = function () {
      return getCoarseConformation(this.kind, this.model);
    };

    return Coarse;
  }();

  function getCoarseConformation(kind, model) {
    return kind === 1
    /* Spheres */
    ? model.coarseConformation.spheres : model.coarseConformation.gaussians;
  }

  function CoarseProperties() {
    return BaseProperties();
  }

  var Spheres =
  /** @class */
  function (_super) {
    __extends(Spheres, _super);

    function Spheres() {
      return _super !== null && _super.apply(this, arguments) || this;
    }

    return Spheres;
  }(Coarse);

  Unit.Spheres = Spheres;

  var Gaussians =
  /** @class */
  function (_super) {
    __extends(Gaussians, _super);

    function Gaussians() {
      return _super !== null && _super.apply(this, arguments) || this;
    }

    return Gaussians;
  }(Coarse);

  Unit.Gaussians = Gaussians;

  function createCoarse(id, invariantId, chainGroupId, traits, model, kind, elements, conformation, props) {
    return new Coarse(id, invariantId, chainGroupId, traits, model, kind, elements, conformation, props)
    /** lets call this an ugly temporary hack */
    ;
  }

  function areSameChainOperatorGroup(a, b) {
    return a.chainGroupId === b.chainGroupId && a.conformation.operator.name === b.conformation.operator.name;
  }

  Unit.areSameChainOperatorGroup = areSameChainOperatorGroup;

  function areOperatorsEqual(a, b) {
    return Mat4.areEqual(a.conformation.operator.matrix, b.conformation.operator.matrix, 1e-6);
  }

  Unit.areOperatorsEqual = areOperatorsEqual;

  function areConformationsEqual(a, b) {
    if (!SortedArray.areEqual(a.elements, b.elements)) return false;
    return isSameConformation(a, b.model);
  }

  Unit.areConformationsEqual = areConformationsEqual;

  function tryRemapBonds(a, old, model) {
    // TODO: should include additional checks?
    var _a;

    if (!old) return void 0;
    if (a.model.atomicConformation.id === model.atomicConformation.id) return old;
    var oldIndex = IndexPairBonds.Provider.get(a.model);

    if (oldIndex) {
      var newIndex = IndexPairBonds.Provider.get(model); // TODO: check the actual indices instead of just reference equality?

      if (!newIndex || oldIndex === newIndex) return old;
      return void 0;
    }

    if ((_a = old.props) === null || _a === void 0 ? void 0 : _a.canRemap) {
      return old;
    }

    return isSameConformation(a, model) ? old : void 0;
  }

  function isSameConformation(u, model) {
    var coordsHistory = Model.CoordinatesHistory.get(Model.getRoot(model));
    if (coordsHistory) return coordsHistory.areEqual(u.elements, u.kind, model);
    var xs = u.elements;
    var _a = u.conformation.coordinates,
        xa = _a.x,
        ya = _a.y,
        za = _a.z;

    var _b = getModelConformationOfKind(u.kind, model),
        xb = _b.x,
        yb = _b.y,
        zb = _b.z;

    for (var i = 0, _i = xs.length; i < _i; i++) {
      var u_1 = xs[i];
      if (xa[u_1] !== xb[u_1] || ya[u_1] !== yb[u_1] || za[u_1] !== zb[u_1]) return false;
    }

    return true;
  }

  Unit.isSameConformation = isSameConformation;

  function getModelConformationOfKind(kind, model) {
    return kind === 0
    /* Atomic */
    ? model.atomicConformation : kind === 1
    /* Spheres */
    ? model.coarseConformation.spheres : model.coarseConformation.gaussians;
  }

  Unit.getModelConformationOfKind = getModelConformationOfKind;

  function getConformation(u) {
    return getModelConformationOfKind(u.kind, u.model);
  }

  Unit.getConformation = getConformation;

  function getModelHierarchyOfKind(kind, model) {
    return kind === 0
    /* Atomic */
    ? model.atomicHierarchy : kind === 1
    /* Spheres */
    ? model.coarseHierarchy.spheres : model.coarseHierarchy.gaussians;
  }

  Unit.getModelHierarchyOfKind = getModelHierarchyOfKind;

  function getHierarchy(u) {
    return getModelHierarchyOfKind(u.kind, u.model);
  }

  Unit.getHierarchy = getHierarchy;
})(Unit || (Unit = {}));

export { Unit };